import * as Popover from '@radix-ui/react-popover';
import {
  Activity,
  AlertCircle,
  BarChart3,
  ChevronUp,
  Gauge,
  HeartPulse,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  ServerCog,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Link, Outlet, useLocation } from 'react-router';

import { DateRangeControls } from '@/components/dateRangeControls';
import { FilterBar } from '@/components/filterBar';
import type { PageFilterKey } from '@/components/filterBar';
import { PageHeader } from '@/components/pageHeader';
import { ErrorState } from '@/components/stateViews';
import { EnvironmentBadge } from '@/components/statusBadges';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Drawer } from '@/components/ui/drawer';
import { Separator } from '@/components/ui/separator';
import { useSummaryQuery } from '@/hooks/useObservabilityQueries';
import type { AnalyticsUrlState } from '@/lib/urlState';
import { environmentValues, useAnalyticsUrlState } from '@/lib/urlState';
import { useAnalyticsAuth } from '@/lib/useAnalyticsAuth';
import { ClassNames } from '@/shared/classNames';

type NavigationItem = {
  icon: LucideIcon;
  label: string;
  path: string;
};

type PageContract = {
  description: string;
  eyebrow: string;
  filters: PageFilterKey[];
  title: string;
};

const navigationGroups: { items: NavigationItem[]; label: string }[] = [
  {
    items: [
      { icon: LayoutDashboard, label: 'Overview', path: '/' },
      { icon: AlertCircle, label: 'Failures', path: '/failures' },
      { icon: Activity, label: 'Performance', path: '/performance' },
      { icon: Gauge, label: 'Frontend experience', path: '/frontend-experience' },
    ],
    label: 'Monitor',
  },
  {
    items: [
      { icon: Users, label: 'Usage', path: '/usage' },
      { icon: ServerCog, label: 'Services', path: '/services' },
      { icon: Search, label: 'Explore', path: '/explore' },
      { icon: HeartPulse, label: 'System', path: '/system' },
    ],
    label: 'Understand',
  },
  {
    items: [{ icon: Users, label: 'User access', path: '/users' }],
    label: 'Manage',
  },
];

const pageContracts: Record<string, PageContract> = {
  '/': {
    description: 'Attention, recent activity, and whether the evidence can be trusted.',
    eyebrow: 'Monitor',
    filters: ['analysis', 'service'],
    title: 'Overview',
  },
  '/explore': {
    description: 'Compare bounded operational questions or inspect contextual logs.',
    eyebrow: 'Understand',
    filters: ['analysis', 'service', 'runtime', 'source'],
    title: 'Explore',
  },
  '/failures': {
    description: 'Investigate, transition, and correlate the operator work queue.',
    eyebrow: 'Monitor',
    filters: ['analysis', 'service', 'runtime', 'failureSeverity', 'failureGroup', 'failureRecurrence'],
    title: 'Failures',
  },
  '/frontend-experience': {
    description: 'Browser sessions, routes, web vitals, and client-side failures.',
    eyebrow: 'Monitor',
    filters: ['sessionRoute', 'sessionBrowser', 'sessionDevice', 'sessionVital', 'sessionError'],
    title: 'Frontend experience',
  },
  '/performance': {
    description: 'Runs, duration, queue, startup, and span timing in one workspace.',
    eyebrow: 'Monitor',
    filters: ['analysis', 'service', 'runtime', 'runStatus', 'workloadSize', 'workerVersion', 'percentile'],
    title: 'Performance',
  },
  '/system': {
    description: 'Signal definitions, collection coverage, and environment health.',
    eyebrow: 'Understand',
    filters: ['analysis', 'service', 'runtime', 'source'],
    title: 'System',
  },
  '/services': {
    description: 'Lambda, API, Fargate, and queue operations with bounded performance, capacity, and cost evidence.',
    eyebrow: 'Understand',
    filters: [],
    title: 'Services',
  },
  '/usage': {
    description: 'Product activity, users, projects, files, and storage.',
    eyebrow: 'Understand',
    filters: ['analysis'],
    title: 'Usage',
  },
  '/users': {
    description: 'Review accounts and make safe SaaS access decisions.',
    eyebrow: 'Manage',
    filters: [],
    title: 'User access',
  },
};

const isActivePath = (currentPath: string, itemPath: string): boolean =>
  itemPath === '/' ? currentPath === '/' : currentPath.startsWith(itemPath);

const operatorInitials = (value: string): string => {
  const normalized =
    value
      .split('@')[0]
      ?.replaceAll(/[._+-]+/g, ' ')
      .trim() ?? '';
  const parts = normalized.split(/\s+/).filter(Boolean);
  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('');
};

const EnvironmentControl = ({
  compact = false,
  onChange,
  value,
}: {
  compact?: boolean;
  onChange: (value: AnalyticsUrlState['env']) => void;
  value: AnalyticsUrlState['env'];
}) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover.Root onOpenChange={setOpen} open={open}>
      <Popover.Trigger asChild>
        <Button
          aria-label={`Change environment, current ${value}`}
          className={ClassNames('w-full', compact ? 'px-2' : 'justify-between')}
          variant="ghost"
        >
          <EnvironmentBadge value={value} />
          {compact ? null : <span className="text-muted text-xs">Environment</span>}
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align={compact ? 'start' : 'center'}
          className="border-line bg-raised shadow-raised z-50 min-w-40 rounded-lg border p-1"
          sideOffset={8}
        >
          <p className="text-muted px-2 py-1.5 text-xs font-semibold tracking-wide uppercase">Data environment</p>
          {environmentValues.map((environment) => (
            <Button
              className="w-full justify-between"
              key={environment}
              onClick={() => {
                onChange(environment);
                setOpen(false);
              }}
              variant={environment === value ? 'secondary' : 'ghost'}
            >
              {environment}
              {environment === value ? <span className="text-muted text-xs">Current</span> : null}
            </Button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const AccountMenu = ({ compact = false, identity, onSignOut }: { compact?: boolean; identity: string; onSignOut: () => void }) => (
  <Popover.Root>
    <Popover.Trigger asChild>
      <Button aria-label={`Operator account, ${identity}`} className="w-full justify-between px-2" variant="ghost">
        <span className="bg-action text-raised grid size-8 shrink-0 place-items-center rounded-full text-xs font-semibold">
          {operatorInitials(identity) || 'OP'}
        </span>
        {compact ? null : (
          <>
            <span className="text-ink min-w-0 flex-1 truncate text-left text-xs">{identity}</span>
            <ChevronUp aria-hidden="true" className="text-muted size-4" />
          </>
        )}
      </Button>
    </Popover.Trigger>
    <Popover.Portal>
      <Popover.Content align="start" className="border-line bg-raised shadow-raised z-50 w-64 rounded-lg border p-2" sideOffset={8}>
        <p className="text-muted truncate px-2 py-2 text-xs">{identity}</p>
        <Separator />
        <Button className="mt-1 w-full justify-start" onClick={onSignOut} variant="ghost">
          <LogOut aria-hidden="true" className="size-4" />
          Sign out
        </Button>
      </Popover.Content>
    </Popover.Portal>
  </Popover.Root>
);

const NavigationFailureCount = ({ compact, count }: { compact: boolean; count?: number }) => {
  if (count === undefined) {
    return null;
  }

  if (compact) {
    return <span aria-label={`${count} open failures`} className="bg-critical absolute mt-[-1.75rem] ml-7 size-2 rounded-full" />;
  }

  return (
    <Badge className="ml-auto" tone={count > 0 ? 'red' : 'green'}>
      {count}
    </Badge>
  );
};

const Navigation = ({
  compact = false,
  currentPath,
  failureCount,
  onNavigate,
  scopeSearch,
}: {
  compact?: boolean;
  currentPath: string;
  failureCount?: number;
  onNavigate?: () => void;
  scopeSearch: string;
}) => (
  <nav aria-label="Analytics navigation" className="grid gap-4">
    {navigationGroups.map((group) => (
      <div className="grid gap-1" key={group.label}>
        <p className={ClassNames('text-muted px-3 text-[0.6875rem] font-semibold tracking-widest uppercase', compact && 'sr-only')}>
          {group.label}
        </p>
        {group.items.map((item) => {
          const Icon = item.icon;
          const active = isActivePath(currentPath, item.path);
          const itemFailureCount = item.path === '/failures' ? failureCount : undefined;

          return (
            <Link
              aria-current={active ? 'page' : undefined}
              className={ClassNames(
                'text-muted hover:bg-selection hover:text-ink relative flex min-h-10 items-center gap-3 rounded-md px-3 text-sm font-semibold transition-colors',
                active && 'bg-selection text-ink',
                compact && 'justify-center px-2',
              )}
              key={item.path}
              onClick={onNavigate}
              title={compact ? item.label : undefined}
              to={{ pathname: item.path, search: scopeSearch }}
            >
              <Icon aria-hidden="true" className="size-4 shrink-0" />
              <span className={ClassNames(compact && 'sr-only')}>{item.label}</span>
              <NavigationFailureCount compact={compact} count={itemFailureCount} />
            </Link>
          );
        })}
      </div>
    ))}
  </nav>
);

export const AppShell = () => {
  const { logout, operator } = useAnalyticsAuth();
  const [state, setState] = useAnalyticsUrlState();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const summaryQuery = useSummaryQuery(state);
  const page = pageContracts[location.pathname] ?? pageContracts['/'];
  const identity = operator?.email ?? operator?.username ?? 'Analytics operator';
  const failureCount = summaryQuery.data?.failures.openFailures;
  const scopeSearch = new URLSearchParams({
    end: state.end.toISOString(),
    env: state.env,
    start: state.start.toISOString(),
  }).toString();

  useEffect(() => {
    const search = new URLSearchParams(globalThis.location.search);
    const serializedStart = search.get('start');
    const serializedEnd = search.get('end');
    const normalizedStart = state.start.toISOString();
    const normalizedEnd = state.end.toISOString();
    const missingRequiredDefaults = !search.has('env') || serializedStart === null || serializedEnd === null;
    const windowNeedsSerialization =
      (serializedStart !== null && serializedStart !== normalizedStart) || (serializedEnd !== null && serializedEnd !== normalizedEnd);

    if (missingRequiredDefaults || windowNeedsSerialization) {
      void setState({
        end: state.end,
        env: state.env,
        start: state.start,
      });
    }
  }, [location.search, setState, state.end, state.env, state.start]);

  const updateState = (nextState: Partial<AnalyticsUrlState>) => {
    void setState(nextState);
  };

  return (
    <div className="bg-canvas min-h-screen">
      <aside className="border-line bg-sidebar fixed inset-y-0 left-0 z-20 hidden w-18 flex-col border-r lg:flex xl:w-56">
        <div className="grid gap-3 p-3">
          <div className="flex min-h-10 items-center gap-3 px-2">
            <span className="bg-action text-raised grid size-8 shrink-0 place-items-center rounded-lg">
              <BarChart3 aria-hidden="true" className="size-4" />
            </span>
            <div className="hidden min-w-0 xl:block">
              <p className="text-ink truncate text-sm font-semibold">Scatterin</p>
              <p className="text-muted text-xs">Analytics</p>
            </div>
          </div>
          <div className="xl:hidden">
            <EnvironmentControl compact onChange={(env) => updateState({ env, runOffset: 0, sessionOffset: 0 })} value={state.env} />
          </div>
          <div className="hidden xl:block">
            <EnvironmentControl onChange={(env) => updateState({ env, runOffset: 0, sessionOffset: 0 })} value={state.env} />
          </div>
        </div>
        <Separator />
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <div className="xl:hidden">
            <Navigation compact currentPath={location.pathname} failureCount={failureCount} scopeSearch={scopeSearch} />
          </div>
          <div className="hidden xl:block">
            <Navigation currentPath={location.pathname} failureCount={failureCount} scopeSearch={scopeSearch} />
          </div>
        </div>
        <div className="border-line border-t p-3">
          <div className="xl:hidden">
            <AccountMenu compact identity={identity} onSignOut={() => void logout()} />
          </div>
          <div className="hidden xl:block">
            <AccountMenu identity={identity} onSignOut={() => void logout()} />
          </div>
        </div>
      </aside>

      <Drawer
        description="Choose an operator destination or change the active environment."
        onOpenChange={setDrawerOpen}
        open={drawerOpen}
        title="Scatterin Analytics"
      >
        <div className="flex min-h-full flex-col gap-4">
          <EnvironmentControl onChange={(env) => updateState({ env, runOffset: 0, sessionOffset: 0 })} value={state.env} />
          <Separator />
          <div className="min-h-0 flex-1 overflow-y-auto">
            <Navigation
              currentPath={location.pathname}
              failureCount={failureCount}
              onNavigate={() => setDrawerOpen(false)}
              scopeSearch={scopeSearch}
            />
          </div>
          <div className="mt-auto">
            <AccountMenu identity={identity} onSignOut={() => void logout()} />
          </div>
        </div>
      </Drawer>

      <div className="lg:pl-18 xl:pl-56">
        <header className="border-line bg-canvas/94 sticky top-0 z-10 border-b backdrop-blur">
          <div className="mx-auto flex max-w-[100rem] items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
            <Button
              aria-label="Open navigation"
              className="shrink-0 px-2.5 lg:hidden"
              onClick={() => setDrawerOpen(true)}
              variant="secondary"
            >
              <Menu aria-hidden="true" className="size-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <PageHeader description={page.description} eyebrow={page.eyebrow} title={page.title} />
            </div>
            <div className="hidden items-center gap-2 md:flex">
              {location.pathname === '/users' ? null : (
                <DateRangeControls onChange={(window) => updateState({ ...window, runOffset: 0, sessionOffset: 0 })} state={state} />
              )}
              <FilterBar filters={page.filters} mode="trigger" onChange={updateState} state={state} />
            </div>
          </div>
          <div className="border-line/70 mx-auto flex max-w-[100rem] items-center justify-between gap-2 border-t px-4 py-2 sm:px-6 md:hidden">
            {location.pathname === '/users' ? (
              <span className="text-muted text-xs">Environment scope</span>
            ) : (
              <DateRangeControls onChange={(window) => updateState({ ...window, runOffset: 0, sessionOffset: 0 })} state={state} />
            )}
            <FilterBar filters={page.filters} mode="trigger" onChange={updateState} state={state} />
          </div>
        </header>
        <main className="mx-auto grid max-w-[100rem] gap-5 px-4 py-5 sm:px-6 lg:px-8">
          <div className="text-muted flex flex-wrap items-center gap-2 text-xs">
            <EnvironmentBadge value={state.env} />
            {location.pathname === '/users' ? null : (
              <>
                <span aria-hidden="true">·</span>
                <span className="numeric">Window ends {state.end.toLocaleString()}</span>
              </>
            )}
          </div>
          <FilterBar filters={page.filters} mode="active" onChange={updateState} state={state} />
          <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
              <ErrorState
                message={error instanceof Error ? error.message : 'This analytics destination failed'}
                onRetry={resetErrorBoundary}
              />
            )}
            resetKeys={[location.pathname]}
          >
            <Outlet context={{ setState: updateState, state }} />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
};
